/** @jsx isolateComponent */

import { Colors, Spacer, VisuallyHidden } from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import {
  skipLinkHref,
  topNavFallbackData,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '../../constants';

import { useUniversalNavContext } from '../../hooks';
import SkipLink from '../SkipLink';
import { staticProductTabBottomLinks } from '../UniversalDynamicTopbar/UniversalDynamicTopbar';
import UniversalDynamicTopbarLink from '../UniversalDynamicTopbar/UniversalDynamicTopbarLink';
import type { UniversalTopbarProps } from '../UniversalTopbar';

import AccountActions from '../../components/UniversalTopbar/AccountActions';
import SearchForm from '../../components/UniversalTopbar/SearchForm';

import type { Props as SearchFormProps } from '../../components/UniversalTopbar/SearchForm';

export type Props = UniversalTopbarProps & { shouldRenderHiddenMenu?: boolean };

const Styles = labelStyles('TopbarLoadingState', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `${Colors.neutral100}`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid ${Colors.neutral300}`,
    },
  ],
  header: ({ height, size }: Props) => [
    {
      alignItems: 'stretch',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      width: '100%',
      backgroundColor: `${Colors.neutral100}`,
      height,
      padding: '0 20px',
      // When the sidebar would usually render (with the toggle)
      // This padding accounts for the size of the toggle button
      // and the gap between the toggle button and logo
      paddingLeft: 71,
      maxWidth: 1200,
      '@media screen and (min-width: 1040px)': {
        paddingLeft: 20,
      },
    },
    withModifier(
      `${size}-${height}`,
      {
        small: {
          gap: 5,
          height,
          padding: '0 10px',
          // When the sidebar would usually render (with the toggle)
          // This padding accounts for the size of the toggle button
          // and the gap between the toggle button and logo
          paddingLeft: 59,
        },
        medium: {
          height,
          padding: '0 20px',
          // When the sidebar would usually render (with the toggle)
          // This padding accounts for the size of the toggle button
          // and the gap between the toggle button and logo
          paddingLeft: 71,
          '@media screen and (min-width: 1040px)': {
            paddingLeft: 20,
          },
        },
      }[size]
    ),
  ],
  group: ({ size }: Props) => [
    {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      justifySelf: 'flex-end',
    },
    withModifier(
      size,
      {
        small: { gap: 5 },
        medium: { gap: 17 },
      }[size]
    ),
  ],
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

/**
 * @private
 * Renders loading state for the dynamic navigation top bar with Zapier logo, search,
 * and account actions.
 */
const TopbarLoadingState = ({
  shouldRenderHiddenMenu = true,
  ...props
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const logoSize = {
    width: 105,
    height: 28,
  };
  return (
    <div css={Styles.root}>
      <div css={Styles.header(props)} data-testid="universalTopbar">
        <div css={Styles.group(props)}>
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper}>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink}>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent}>
          {shouldRenderHiddenMenu ? (
            <VisuallyHidden>
              <nav aria-label="Site Links" id="site-nav">
                <ul>
                  <UniversalDynamicTopbarLink
                    ariaControls="Product"
                    bottomLinks={staticProductTabBottomLinks}
                    href={null}
                    isSelected={false}
                    isStaticProductTab={true}
                    key="Product"
                    // Submenu must be defined in order to render MegaMenu inside UniversalDynamicTopbarLink
                    submenu={[]}
                  >
                    Product
                  </UniversalDynamicTopbarLink>
                  {topNavFallbackData?.topBarMenu?.map((link) => (
                    <UniversalDynamicTopbarLink
                      href={link.href}
                      key={link.text}
                      isSelected={false}
                      submenu={link.submenu}
                    >
                      {link.text}
                    </UniversalDynamicTopbarLink>
                  ))}
                </ul>
              </nav>
            </VisuallyHidden>
          ) : (
            <Spacer />
          )}
        </div>
        <div
          aria-label="Manage your account and search apps"
          css={Styles.group(props)}
          role="form"
        >
          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

TopbarLoadingState.defaultProps = {
  logoAriaLabel: 'Go to the Zapier Homepage',
  logoHref: '/',
  onLogoClick: () => {},
  renderAccountMenu: (props: Props) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications: () => null,
  renderSearchForm: (props: SearchFormProps) => (
    <SearchForm
      isSearching={props.isSearching}
      onClose={props.onClose}
      searchInputId={props.searchInputId}
    />
  ),
  selectedHref: '',
  shouldRenderLogo: true,
  height: UNIVERSAL_TOPBAR_HEIGHT,
  size: 'medium',
  skipLinkHref,
};

export default TopbarLoadingState;
